import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { coinIconSrc, makeCoinImageOnError } from "../WithdrawPage/withdrawPageHelpers";
import {
  resolveDepositHistoryAddressExplorerHref,
  resolveDepositHistoryTxExplorerHref,
} from "./depositHistoryShared";
import { DepositHistoryStatus } from "./DepositSenderConfirm";
import "../WithdrawPage/withdrawal.css";

/** API may return REJECTED or misspellings such as REJCTED */
function isRejectedDepositStatus(action) {
  const n = String(action || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, "");
  return n === "REJECTED" || n === "REJCTED";
}

const REJECTED_SCREENING_DESCRIPTION =
  "Blocked by screening. Our team will review the transaction shortly and you will get an update via email.";

function depositDetailStatusTone(label) {
  const t = String(label || "").toLowerCase();
  if (!t || t === "—") return "neutral";
  if (/(completed|confirmed|success|credited|done|approve|approved)/i.test(t)) return "success";
  if (/(pending|processing|confirming|queued|wait)/i.test(t)) return "pending";
  if (/(fail|failed|reject|rejected|rejcted|cancel|error|expired)/i.test(t)) return "danger";
  return "info";
}

function buildDepositDetailSteps(row) {
  const t = row.date || "—";
  const status = row.action || "—";
  const tone = row.actionTone || "neutral";
  if (tone === "danger") {
    return [
      { title: "Deposit recorded", time: t, emphasized: false },
      { title: status !== "—" ? status : "Deposit unsuccessful", subtitle: "", time: t, emphasized: true },
    ];
  }
  if (tone === "success") {
    return [
      { title: "Deposit received", time: t, emphasized: false },
      { title: status !== "—" ? status : "Credited", time: t, emphasized: true },
    ];
  }
  return [
    { title: "Deposit submitted", time: t, emphasized: false },
    {
      title: status !== "—" ? status : "Processing",
      subtitle: "You will receive an email when this deposit completes.",
      time: t,
      emphasized: true,
    },
  ];
}

const WithdrawHistCopyIcon = () => (
  <svg className="withdraw_hist_icon_svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9 3h11v13h-3V6H9V3zM4 8v13h11V8.02L4 8z"
      fill="currentColor"
    />
  </svg>
);

const WithdrawHistExternalIcon = () => (
  <svg className="withdraw_hist_icon_svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5M16.5 3H21m0 0v4.5M21 3 9 15"
    />
  </svg>
);

async function copyText(text, successMsg = "Copied") {
  if (!text) return;
  try {
    await navigator.clipboard.writeText(text);
    alertSuccessMessage(successMsg);
  } catch {
    /* ignore */
  }
}

/**
 * Detail modal for a single deposit history row — not part of the deposit form itself.
 * Opened from Profile → Orders → Deposit History, or from the “Deposit History” list on the Deposit page (`/asset_managemnet/deposit`).
 * Reuses withdrawal-history detail chrome (`withdrawal.css`).
 */
export default function DepositHistoryDetailModal({ open, row, onClose, depositAgainTo = "/asset_managemnet/deposit" }) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !row) return null;

  const steps = buildDepositDetailSteps(row);
  const stepperOutcome =
    row.actionTone === "success" ? "success" : row.actionTone === "danger" ? "danger" : "pending";
  const tone = depositDetailStatusTone(row.action);
  const rejected =
    String(row.status || "").trim().toUpperCase() === "REJECTED" || isRejectedDepositStatus(row.action);
  const addressHref = resolveDepositHistoryAddressExplorerHref(row.explorerLink, row.address);
  const txHref = resolveDepositHistoryTxExplorerHref(row.explorerLink, row.txid);
  const hasAddr = row.address && row.address !== "—";
  const hasTx = row.txid && row.txid !== "—";
  const networkPretty =
    row.chain_full_name && row.chain_full_name !== "—" ? row.chain_full_name : row.network;
  const iconSrc = coinIconSrc({ short_name: row.short_name });

  const onDepositAgain = () => {
    onClose?.();
    navigate(depositAgainTo);
  };

  return (
    <div
      className="withdraw_confirm_overlay withdraw_hist_detail_overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="withdraw_hist_detail_modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="deposit_hist_detail_title_orders"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={onClose}>
          ×
        </button>

        <div className="withdraw_hist_detail_head">
          <h2 id="deposit_hist_detail_title_orders" className="withdraw_hist_detail_title">
            Deposit Details
          </h2>
        </div>

        <div className="withdraw_hist_detail_body">
          <div className="withdraw_hist_stepper" aria-label="Deposit progress" data-outcome={stepperOutcome}>
            {steps.map((s, i, arr) => (
              <div key={`${row.id}-dstep-${i}`} className="withdraw_hist_step">
                <div className="withdraw_hist_step_track" aria-hidden="true">
                  <span className="withdraw_hist_step_dot" />
                  {i < arr.length - 1 ? <span className="withdraw_hist_step_line" /> : null}
                </div>
                <div className="withdraw_hist_step_text">
                  <div className={`withdraw_hist_step_title${s.emphasized ? " is-emphasized" : ""}`}>{s.title}</div>
                  {s.subtitle ? <div className="withdraw_hist_step_sub">{s.subtitle}</div> : null}
                  <div className="withdraw_hist_step_time">{s.time}</div>
                </div>
              </div>
            ))}
          </div>

          {tone === "danger" ? (
            rejected ? (
              <p className="withdraw_hist_step_note withdraw_hist_step_note--danger">{REJECTED_SCREENING_DESCRIPTION}</p>
            ) : (
              <p className="withdraw_hist_step_note withdraw_hist_step_note--danger">
                This deposit did not complete as expected. If funds left your external wallet, contact support with this reference.
              </p>
            )
          ) : row.needsSenderConfirm ? (
            <p className="withdraw_hist_step_note">Balance has not increased.</p>
          ) : tone === "success" ? (
            <p className="withdraw_hist_step_note">Your deposit has been credited. Balances may take a moment to update everywhere.</p>
          ) : (
            <p className="withdraw_hist_step_note">Please note that you will receive an email once this deposit is confirmed.</p>
          )}

          <div className="withdraw_hist_help_links">
            <Link to="/user_profile/create_tickets" className="withdraw_hist_gold_link">
              Report Scam
            </Link>
          </div>

          <div className="withdraw_hist_rows">
            <div className="withdraw_hist_row">
              <span className="withdraw_hist_row_label">Status</span>
              <span className={`withdraw_hist_row_value withdraw_hist_row_value--${tone}`}>
                <DepositHistoryStatus
                  row={row}
                  badgeClassName=""
                  onOpen={onClose}
                />
              </span>
            </div>
            <div className="withdraw_hist_row">
              <span className="withdraw_hist_row_label">Date</span>
              <span className="withdraw_hist_row_value">{row.date}</span>
            </div>
            <div className="withdraw_hist_row">
              <span className="withdraw_hist_row_label">Deposit wallet</span>
              <span className="withdraw_hist_row_value">{row.depositWallet}</span>
            </div>
            <div className="withdraw_hist_row">
              <span className="withdraw_hist_row_label">Coin</span>
              <span className="withdraw_hist_row_value withdraw_hist_row_value--coin">
                <img src={iconSrc} alt="" width={22} height={22} onError={makeCoinImageOnError({ short_name: row.short_name })} />
                {row.short_name}
              </span>
            </div>
            <div className="withdraw_hist_row">
              <span className="withdraw_hist_row_label">Amount</span>
              <span className="withdraw_hist_row_value">
                {row.net_amount} {row.short_name}
              </span>
            </div>
            <div className="withdraw_hist_row">
              <span className="withdraw_hist_row_label">Network</span>
              <span className="withdraw_hist_row_value">{networkPretty}</span>
            </div>
            <div className="withdraw_hist_row">
              <span className="withdraw_hist_row_label">Address</span>
              <span className="withdraw_hist_row_value withdraw_hist_row_value--addr">
                <span className="withdraw_hist_mono">{row.addressShort}</span>
                <button
                  type="button"
                  className="withdraw_hist_icon_btn"
                  aria-label="Copy address"
                  disabled={!hasAddr}
                  onClick={() => (hasAddr ? copyText(row.address, "Address copied") : null)}
                >
                  <WithdrawHistCopyIcon />
                </button>
                <a
                  className={`withdraw_hist_icon_btn withdraw_hist_icon_link${!addressHref ? " is-disabled" : ""}`}
                  href={addressHref || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View address on explorer"
                  aria-disabled={!addressHref}
                  onClick={(e) => {
                    if (!addressHref) e.preventDefault();
                  }}
                >
                  <WithdrawHistExternalIcon />
                </a>
              </span>
            </div>
            <div className="withdraw_hist_row">
              <span className="withdraw_hist_row_label">TxID</span>
              <span className="withdraw_hist_row_value withdraw_hist_row_value--addr">
                <span className="withdraw_hist_mono">{row.txidShort}</span>
                <button
                  type="button"
                  className="withdraw_hist_icon_btn"
                  aria-label="Copy transaction ID"
                  disabled={!hasTx}
                  onClick={() => (hasTx ? copyText(row.txid, "TxID copied") : null)}
                >
                  <WithdrawHistCopyIcon />
                </button>
                <a
                  className={`withdraw_hist_icon_btn withdraw_hist_icon_link${!txHref ? " is-disabled" : ""}`}
                  href={txHref || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View transaction on explorer"
                  aria-disabled={!txHref}
                  onClick={(e) => {
                    if (!txHref) e.preventDefault();
                  }}
                >
                  <WithdrawHistExternalIcon />
                </a>
              </span>
            </div>
          </div>

          <p className="withdraw_hist_chat_row">
            <Link to="/user_profile/create_tickets" className="withdraw_hist_gold_link">
              Need help? Chat with us
            </Link>
          </p>

          {/* <button type="button" className="withdraw_hist_again_btn" onClick={onDepositAgain}>
            Deposit Again
          </button> */}
        </div>
      </div>
    </div>
  );
}
